import type { LotteryDrawResultWChance } from '../../wchance';
import type { LotteryPrizeType, OptionInputNamedSelectableStepType, OptionInputType } from '../constants';

export type LotteryEventArtist = {
  id: number;
  name: string;
};

export type LotteryEventTag = {
  id: number;
  name: string;
};

export type LotteryEventUnit = {
  id: number;
  name: string;
};

export type LimitedEventType = 'all' | 'limited';
export type Status = 'upcoming' | 'is_session' | 'later';
export type DrawType = 'unlimited' | 'only_serial_code' | 'first_serial_code';

// 公開ステータス
export const PublicStatus = {
  public: { value: 'public' },
  pre_public: { value: 'pre_public' },
  private: { value: 'private' },
  draft: { value: 'draft' },
} as const;
export type PublicStatusType = keyof typeof PublicStatus;

export type LotteryPrizeTypeType = keyof typeof LotteryPrizeType;

export type LotteryEvent = {
  id: number;
  name: string;
  imageUrl: string;
  subImageUrl: string;
  startAt: string;
  endAt: string;
  estimatedShippingDate: string | null;
  artist: Array<LotteryEventArtist>;
  unit: Array<LotteryEventUnit>;
  tags: Array<LotteryEventTag>;
  isBenefit: boolean;
  limitedEventType: LimitedEventType;
  status: Status;
  drawFee: number;
};

export type Keyword = {
  tagId: number;
  tagName: string;
};

export type LotteryGoods = {
  id: number;
  name: string;
  isStockLimit: boolean;
  stock: number;
  imageUrl: string;
};

// LotteryBenefitGoods用の型定義
export type LotteryBenefitGoods = {
  id: number;
  name: string;
  nameEn?: string;
  isStockLimit: boolean;
  stock: number;
  imageUrl: string;
};

// LotteryGoodsModalState用の型定義
export type LotteryGoodsModalState = {
  id: number;
  isOpen: boolean;
  imageUrl: string;
  name: string;
  isGot: boolean;
};

export type LotteryPrize = {
  id: number;
  name: string;
  imageUrl: string;
  staging: number;
  stagingKey: string;
  stagingName: string;
  description: string;
  description_en: string;
  odds: number;
  prizeType: string;
  deliveryType: string;
  goods: Array<LotteryGoods>;
  categoryName: string;
  categoryName_en: string;
};

export type LotteryBenefit = {
  id: number;
  name: string;
  imageUrl: string;
  staging: number;
  stagingKey: string;
  stagingName: string;
  description: string;
  description_en: string;
  benefitMultiDrawCount: number;
  prizeType: string;
  deliveryType: string;
  categoryName: string;
  categoryName_en: string;
  goods: Array<LotteryGoods>;
};

export type LotteryWChance = {
  id: number;
  name: string;
  odds: number;
  imageUrl: string;
  description: string;
  prizeType: string;
  deliveryType: string;
  startAt: string;
  endAt: string;
  hasStock: boolean;
  sort: number;
  count: number;
};

export type LotteryEventBranch = {
  id: number;
};

export type LotteryGoodsFileItem = {
  id: number;
  url: string;
  order: number;
};

export type LotteryPrice = { drawFee: number; shippingFee: number; additionalShippingFee: number };

export type LotteryCustomOption = {
  [key: string]: string;
} | null;

export type LotteryEventDetail = {
  id: number;
  name: string;
  name_en: string;
  imageUrl: string;
  imageSpUrl: string;
  subImageUrl: string;
  startAt: string;
  endAt: string;
  artist: Array<LotteryEventArtist>;
  unit: Array<LotteryEventUnit>;
  tags: Array<LotteryEventTag>;
  isBenefit: boolean;
  status: string;
  publicStatus: PublicStatusType;
  drawFee: number;
  isShippingFee: boolean;
  shippingFee: number;
  additionalShippingFee: number;
  additionalShippingFeePerTimes: number;
  price: LotteryPrice;
  description: string;
  description_en: string;
  precautions: string;
  precautions_en: string;
  shippingDescription: string;
  shippingDescription_en: string;
  benefitDescription: string;
  benefitDescription_en: string;
  wchanceAbout: string;
  wchanceAbout_en: string;
  estimatedShippingDate: string | null;
  limitedDrawCount: number;
  isMultiDraw: boolean;
  isNotOverlapDraw: null | 'end_first' | 'infinite';
  drawType: DrawType;
  lotteryEventBranches: Array<LotteryEventBranch>;
  multiDraw: Array<{
    id: number;
    count: number;
    prizeLabelId: number;
    prizeOptionFlag: boolean;
  }>;
  eventType: LimitedEventType;
  wChanceType: 'manual' | 'direct' | 'batch';
  wChanceDescription: string;
  limitedEvent: Array<{
    artistId: number;
    artistName: string;
  }>;
  isGlobal: boolean;
  version: string | null;
  isEnabledTrade: boolean;
  prize: Array<LotteryPrize>;
  relationAccountRequired?: {
    dAccount: boolean;
  };
  benefit: Array<LotteryBenefit>;
  wChance: Array<LotteryWChance>;
  refererUrl: string;
  customOptions?: {
    isDefaultOpenDescription?: string;
    lotteryPrizeBgColor_staging_A?: string;
    hideOdds?: string;
    hideStaging?: string;
    hideEstimatedShipmentDays?: string;
    popUpMessageTitleLotteryEventEntry?: string;
    popUpMessageTextLotteryEventEntry?: string;
  } | null;
  goodsFiles: LotteryGoodsFileItem[];
};

export type GoodsCountNotOverlap = {
  id: number;
  acquiredCount: number;
};

export type LotteryDrawHistoryLineup = {
  id: number;
  odds: number;
  goods: Array<GoodsCountNotOverlap>;
};

export type LotteryDrawHistoryGoodsCountNotOverlap = {
  userDrawCount: number;
  prize: Array<LotteryDrawHistoryLineup>;
  benefit: Array<LotteryDrawHistoryLineup>;
};

export type LotteryDrawHistoryGoodsCountCampaign = {
  userDrawCount: number;
  prize: Array<LotteryDrawHistoryLineup>;
  benefit: Array<LotteryDrawHistoryLineup>;
};

export type LotteryGoodsCollection = {
  goodsId: number;
  goodsName: string;
  imageUrl: string;
  fileUrl: string;
  prizeLabelName: string;
  prizeLabelType: LotteryPrizeTypeType;
  count: number;
  lotteryEventId: number;
  lotteryEventName: string;
  lotteryEventEndAt: string;
  version: string;
  cutoffId: number | null;
  deliveryId: number;
  deliveryType: string;
  deliveryStatus: 'unprocessed' | 'preparing' | 'complete' | 'no_address';
  estimatedShippingDate: string | null;
  prizeOptionEndAt: string;
  prizeOptionFlag: number;
  incompleteOptionFlag: boolean;
  isRepostCampaign: boolean;
  stagingKey: string;
  stagingName: string;
  benefitMultiDrawCount: number;
  lotteryDrawHistoryId: number;
  noSetAddress: boolean;
  isWchance: boolean;
  totalQuantity: number;
  receivedQuantity: number;
  customOptions?: LotteryCustomOption;
  orderId?: string | null;
};

export type LotteryGoodsCollectionStagingInfo = {
  stagingText: string;
  stagingRank: string;
};

export type LotteryEventCollection = {
  lotteryEventId: number;
  lotteryEventName: string;
  lotteryEventEndAt: string;
  artists: Array<LotteryEventArtist>;
  unites: Array<LotteryEventUnit>;
  imageUrl: string;
  goodsCount: number;
  acquiredCount: number;
  estimatedShippingDate: string;
  version: string | null;
  isEnabledTrade: boolean;
  incompleteOptionFlag: boolean;
  prizeOptionEndAt: string;
  customOptions?: LotteryCustomOption;
  cutoffId: number | null;
  prizeOptionFlag: number;
  tradeAt: string | null;
};

export type LotteryHistoryCollection = {
  id: number;
  orderId: number;
  lotteryEventId: number;
  lotteryEventName: string;
  thumbnailUrl: string;
  drawType: string;
  drawCount: number;
  createdAt: string;
  incompleteOptionFlag: boolean;
  version: string;
  optionCutoffAt: string;
  cutoffId: number | null;
  hasPrizeOptionFlag: boolean;
};

// カスタムフォーム種別
export const CustomFormType = {
  email: { text: 'メールアドレス', value: 'email' },
  number: { text: '数値', value: 'number' },
  tel: { text: '電話番号', value: 'tel' },
  text: { text: 'テキスト', value: 'text' },
  textarea: { text: 'テキストエリア', value: 'textarea' },
  url: { text: 'URL', value: 'url' },
  date: { text: '日付', value: 'date' },
  checkbox: { text: 'チェックボックス', value: 'checkbox' },
  radio: { text: 'シングルセレクト', value: 'radio' },
} as const;
export type CustomFormTypeType = keyof typeof CustomFormType;

export type CustomFormPatchInputItem = { hash: string; value: string | string[] };

export type LotteryGoodsCustomFormItem = {
  max: number;
  min: number;
  hash: string;
  type: CustomFormTypeType;
  label: string;
  required: boolean;
  annotation: string;
  placeholder: string;
  value: string | string[] | null;
  options?: {
    label: string;
    value: number;
  }[];
};

export type LotteryGoodsDetailCollection = {
  id: number;
  name: string;
  prizeLabelName: string;
  prizeLabelDescription: string;
  prizeLabelType: LotteryPrizeTypeType;
  prizeText?: string;
  prizeOptionFlag: number;
  prizeOptionDescription: string;
  prizeOptionEndAt: string;
  fileUrl: string;
  thumbnailUrl: string;
  imageUrl: string;
  stagingKey: string;
  stagingName: string;
  benefitMultiDrawCount: number;
  isWchance: boolean;
  downloadStatus: string;
  isRepostCampaign: boolean;
  customForms: LotteryGoodsCustomFormItem[];
  isPity: boolean;
  benefitPityDrawCount: number;
  isV2OptionCompleted: boolean | null;
};

export type LotteryEventDetailCollection = {
  id: number;
  name: string;
  imageUrl: string;
  startAt: string;
  endAt: string;
  isBenefit: boolean;
  limitedEventType: LimitedEventType;
  status: Status;
  drawFee: number;
  shippingDescription: string;
  customOptions?: LotteryCustomOption;
  version: string | null;
  cutoffId: number | null;
};

export type LotteryDeliveryDetailCollection = {
  id: number;
  status: 'unprocessed' | 'inprocess' | 'shipped' | 'complete' | 'return';
  type: 'empty' | 'hand_over' | 'delivery';
  option: string;
  completedAt: string;
  receivedQuantity: number;
  totalQuantity: number;
};

export type CollectionDetail = {
  event: LotteryEventDetailCollection;
  goods: LotteryGoodsDetailCollection;
  delivery: LotteryDeliveryDetailCollection;
};

export type CalcCoinResult = {
  userCoin: number;
  drawCoin: number;
  shippingCoin: number;
};

export type LotteryDetail = {
  lotteryEventId: number;
  multiDrawId: number;
  drawCount: number;
  drawCoin: number;
  shippingCoin: number;
  drawTotalCoin: number;
  prizeLabelName: string;
};

// Atom関数の型定義
export type LotteryDetailAtomHook = () => [LotteryDetail, (detail: LotteryDetail) => void];
export type LotteryDetailLoadingAtomHook = () => [boolean, (loading: boolean) => void];

export type LotteryDrawResultGoods = {
  id: number;
  name: string;
  isNew: boolean;
  imageUrl: string;
  sort: number;
  cutoffId?: number | null;
  isPrizeOption: boolean;
};

export type PrizeConvertedGoodsItem = {
  benefitMultiDrawCount: number;
  stagingText: string;
  stagingRank: string;
  id: number;
  name: string;
  isNew: boolean;
  imageUrl: string;
  sort: number;
  deliveryId?: number;
  isPrizeOption: boolean;
};

export type LotteryDrawResultLineup = {
  id: number;
  name: string;
  stagingId: number;
  stagingKey: string;
  stagingName: string;
  benefitMultiDrawCount: number;
  goods: Array<LotteryDrawResultGoods>;
};

export type LotteryDrawResult = {
  lotteryEventId: number;
  lotteryEventName: string;
  drawType: DrawType;
  lotteryEventBranchId: number;
  lotteryEventBranchMissingImageUrl: string;
  isCampaignDraw: boolean;
  shippingEndDate: string;
  shippingOver: 'reception' | 'over' | 'ok';
  multiDrawId: number;
  historyId: number;
  prize: Array<LotteryDrawResultLineup>;
  benefit: Array<LotteryDrawResultLineup>;
  wChance?: LotteryDrawResultWChance;
  version: string | null;
  isEnabledTrade: boolean;
  hasOptionGoods: boolean;
  prizeOptionEndAt: string | null;
};

export type LotteryEventBranchDetail = {
  id: number;
  lotteryEventId: number;
  name: string;
  imageUrl: string;
  subImageUrl: string;
  startAt: string;
  endAt: string;
  artist: Array<LotteryEventArtist>;
  unit: Array<LotteryEventUnit>;
  tags: Array<LotteryEventTag>;
  isBenefit: boolean;
  drawType: DrawType;
  status: string;
  description: string;
  precautions: string;
  shippingDescription: string;
  benefitDescription: string;
  prize: Array<LotteryPrize>;
  benefit: Array<LotteryBenefit>;
  estimatedShippingDate: string | null;
  customOptions?: LotteryCustomOption;
};

// 10円のコインメニュー
export type CoinMenu10Item = {
  id: number;
  name: string;
  description: string;
  productId: string;
  taxIncludedPrice: number;
  coin: number;
  publishedAt: string;
  closedAt: string;
};

export type UserAddress = {
  id: number;
  fullName: string;
  kanaName: string;
  phone: string;
  zip: string;
  company: string;
  country: string;
  province: string;
  city: string;
  address1: string;
  address2: string;
};

export type MultiDrawItem = {
  id: number;
  count: number;
  prizeLabelId: number;
  name?: string;
};

export type LotteryMe = {
  id: number;
  name: string;
  imageUrl: string;
  profile: string;
  coin: number;
  setAddress: boolean;
  unreadMessageBoxCount: number;
  age: number;
  gender: string;
};

// カスタムフォーム種別
export const RepostCampaignGetType = {
  select: { value: 'select' },
  all: { value: 'all' },
} as const;

export type RepostCampaignGetTypeType = keyof typeof RepostCampaignGetType;

export type RepostCampaignDetail = {
  id: number;
  name: string;
  startAt: string;
  endAt: string;
  setAddress: boolean;
  getType: RepostCampaignGetTypeType | null;
  event: {
    id: number;
    name: string;
    imageUrl: string;
    imageSpUrl: string;
    subImageUrl: string;
    startAt: string;
    endAt: string;
    version: string | null;
  };
  prize: LotteryDrawResultLineup;
};

export type BannerIndex = Array<{
  id: number;
  imageUrl: string;
  url: string;
}>;

// LotteryCard用の型定義
export type LotteryCard = {
  id: number;
  lotteryTitle: string;
  imageUrl: string;
  status: string;
  startAt: string;
  endAt: string;
  drawFee: number;
};

// LotteryDetailKv用の型定義
export type LotteryDetailKvProps = {
  pcKvUrl: string;
  spKvUrl: string;
  variant?: 'default' | 'raffle' | 'goodsmilekuji';
};

// API関連の型定義
export type PostDrawLotsParams = {
  lotteryEventId: number;
  lotteryEventMultiDrawId?: number;
  drawType: 'single' | 'multi';
};

export type PostDrawLotsResponse = {
  result: {
    lotteryDrawHistoryId: number;
  };
};

// State管理の型定義
export type LotteryDetailState = {
  lotteryEventId: number;
  multiDrawId: number;
  drawCount: number;
  drawCoin: number;
  shippingCoin: number;
  drawTotalCoin: number;
  prizeLabelName: string;
};

// Purchase Action型定義
export type PurchaseActionFunction = (coinMenu10Item: CoinMenu10Item, missingCoin: number) => Promise<void>;

// GoMainOneTimeAuth型定義
export type GoMainOneTimeAuthFunction = ({
  redirectPath,
  token,
  lotteryEventId,
  shouldTrack,
}: {
  redirectPath?: string;
  token?: string;
  lotteryEventId?: number;
  shouldTrack?: boolean;
}) => Promise<void>;

export type LotteryTradeMyGoods = {
  minTradeRequiredCount: number;
  labels: {
    id: number;
    name: string;
    stagingKey: string;
    stagingName: string;
    tradeRequiredCount: number;
    goods: {
      id: number;
      name: string;
      imageUrl: string;
      acquiredCount: number;
      personalizeFlag: boolean;
    }[];
  }[];
};

export type TradeDesiredGoods = {
  hashKey: string;
  minTradeRequiredCount: number;
  labels: {
    id: number;
    name: string;
    stagingKey: string;
    stagingName: string;
    tradeRequiredCount: number;
    tradeableInCount: number;
    outGoodsCount: number;
    goods: {
      id: number;
      name: string;
      imageUrl: string;
      acquiredCount: number;
      personalizeFlag: boolean;
      isAcquired: boolean;
    }[];
  }[];
};

export type TradeRequest = {
  event: {
    id: number;
    name: string;
    imageUrl: string;
    imageSpUrl: string;
  };
  tradeRequestId: string;
  tradeAt: string;
  tradeStatus: string;
};

export type TradeRequestDetail = {
  tradeRequestId: string;
  tradeAt: string;
  tradeStatus: string;
  sets: {
    id: number;
    outGoods: {
      id: number;
      name: string;
      count: number;
      imageUrl: string;
      label: {
        id: number;
        stagingKey: string;
        stagingName: string;
      };
    }[];
    inGoods: {
      id: number;
      name: string;
      count: number;
      imageUrl: string;
      label: {
        id: number;
        stagingKey: string;
        stagingName: string;
      };
    }[];
    createdAt: string;
  }[];
};

export type LotteryTrade = {
  event: {
    id: number;
    name: string;
    tradeAt: string;
  };
  goods: {
    wonCount: number;
    totalAcquiredCount: number;
    tradeableCount: number;
    outCount: number;
    minTradeRequiredCount: number;
  };
  isEnabledTrade: boolean;
};

export type LotteryEventIndexPaging = {
  id: number;
  name: string;
  nameEn: string;
  imageUrl: string;
  subImageUrl: string;
  startAt: string;
  endAt: string;
  estimatedShippingDate: string | null;
  artist: Array<LotteryEventArtist>;
  unit: Array<LotteryEventUnit>;
  tags: Array<LotteryEventTag>;
  isBenefit: boolean;
  limitedEventType: LimitedEventType;
  status: Status;
  drawFee: number;
  version: string | null;
  isEnabledTrade: boolean;
  hasTradeHistory: boolean | null;
  tradeAt: string;
};

export type TradeConfirm = {
  hashKey: string;
  sets: {
    outGoods: {
      id: number;
      name: string;
      count: number;
      imageUrl: string;
      label: {
        id: number;
        stagingKey: string;
        stagingName: string;
      };
    }[];
    inGoods: {
      id: number;
      name: string;
      count: number;
      imageUrl: string;
      label: {
        id: number;
        stagingKey: string;
        stagingName: string;
      };
    }[];
  }[];
};

export type DeliveryManagement = {
  id: number;
  event: {
    id: number;
    name: string;
    imageUrl: string;
    imageSpUrl: string;
  };
  delivery: {
    estimatedShippingDate: string | null;
    status: string;
    acquiredCount: number;
  };
};

export type LotteryOptionGoods = {
  lotteryDrawHistoryId: number;
  wChance: {
    id: number;
    name: string;
    wChanceType: string;
    wChanceDescription: string;
    imageUrl: string;
    description: string;
    prizeType: string;
    deliveryType: string;
    startAt: string;
    endAt: string;
    hasStock: boolean;
    count: number;
    isSurveyCompleted: boolean;
    surveyUrl: string;
    surveyId: number;
    stagingRank: number | null;
    cutinType: string | null;
    cutinUrl: string | null;
  };
  labels: {
    id: number;
    name: string;
    stagingKey: string;
    stagingName: string;
    isBenefit: boolean;
    isPity: boolean;
    isWchance: boolean;
    benefitMultiDrawCount: number | null;
    benefitPityDrawCount: number | null;
    goods: {
      id: number;
      name: string;
      imageUrl: string;
      optionType: string;
      inputtedCount: number;
      totalAcquiredCount: number;
      isOptionCompleted: boolean;
      optionCutoffAt: string;
      cutoffId: number;
      orderId: string;
    }[];
  }[];
};

export type LotteryOptionGoodsForm = {
  id: number;
  name: string;
  imageUrl: string;
  totalAcquiredCount: number;
  label: {
    id: number;
    name: string;
    stagingKey: string;
    stagingName: string;
    isBenefit: boolean;
    isPity: boolean;
    isWchance: boolean;
    benefitMultiDrawCount: number | null;
    benefitPityDrawCount: number | null;
  };
  optionInfo: {
    type: 'named' | 'selectable' | 'named_selectable';
    named: {
      hash: string;
      value: string;
      isSystemInput: boolean;
    }[];
    selectable: {
      max: string;
      min: string;
      hash: string;
      type: string;
      label: string;
      required: boolean;
      annotation: string;
      placeholder: string;
      value: string;
      options: {
        value: number;
        label: string;
      }[];
      isSystemInput: boolean;
      imageUrl: string;
    }[];
    namedSelectable: {
      max: string;
      min: string;
      hash: string;
      type: string;
      label: string;
      required: boolean;
      annotation: string;
      placeholder: string;
      value: string;
      options: {
        value: number;
        label: string;
      }[];
      isSystemInput: boolean;
      imageUrl: string;
      notes: {
        hash: string;
        value: string;
        isSystemInput: boolean;
      }[];
    }[];
  };
  optionCutoffAt: string;
  prizeOptionText: string | null;
  optionDefaultSetType: 'name' | 'input' | null;
};

export type DeliveryManagementDetail = {
  id: number;
  shippingInstructionId: string;
  event: {
    id: number;
    name: string;
  };
  delivery: {
    estimatedShippingDate: string | null;
    status: string;
    acquiredCount: number;
    address: {
      fullName: string;
      zip: string;
      province: string;
      city: string;
      address1: string;
      address2: string | null;
      memoForUser: string | null;
    };
  };
  items: {
    id: number;
    lotteryPrizeGoodsId: number;
    name: string;
    imageUrl: string;
    stagingKey: string;
    stagingName: string;
    status: string;
    quantity: number;
    isOptionInput: boolean | null;
    v2LotteryFormInputId: number | null;
    isPity: number;
    isWchance: number;
    isRepostCampaign: number;
    benefitMultiDrawCount: number;
    benefitPityDrawCount: number;
  }[];
};

export type TradeGoodsListItem = {
  id: number;
  name: string;
  imageUrl: string;
  acquiredCount: number;
  personalizeFlag: boolean;
};

export type TradeGoodsLabel = {
  id: number;
  name: string;
  stagingKey: string;
  stagingName: string;
  tradeRequiredCount: number;
  goods: TradeGoodsListItem[];
};

export type TradeGoodsList = {
  minTradeRequiredCount: number;
  labels: TradeGoodsLabel[];
};

// オプション入力区分
export type OptionInputTypeType = keyof typeof OptionInputType;

// オプション入力の選べる＆宛名オプション内のStep区分
export type OptionInputNamedSelectableStepTypeType = keyof typeof OptionInputNamedSelectableStepType;
